import { useState, useRef } from "react"
import styles from "./AdventureMode.module.css"
import { Col, Row } from "antd"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

// context
import { useTheme } from "context/AntdThemeContext"
// ui
import { Card } from "ui/AdventureModeSelect/Card"
import { ActionButtons } from "ui/ActionButtons"
import { MediaUpload } from "ui/MediaUpload"
import DSTab from "ui/DSTab"
import { Icon } from "ui/Icon"
import ImageCropper from "ui/ImageCropper"
// constants
import { themes } from "constants/theme"
// store
import { updateUser, updateGuide, getUser } from "store/user"
import { SettingsTitle } from "../ProSettings/SettingsTitle"
import { toaster } from "components/common/Toast"

const TABS = {
  theme: "theme",
  customBranding: "custom_branding"
}

export const AdventureMode = () => {
  const { t } = useTranslation()

  const { theme, switchTheme, saveThemeToStorage } = useTheme()
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.auth)
  const guide = useSelector((state) => state.user?.guide)
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get("tab") || TABS.customBranding

  const setActiveTab = (tab) => {
    setSearchParams({ tab })
  }
  const [mode, setMode] = useState(theme)
  const prevModeRef = useRef(mode)
  const savedLogoUrl = guide?.logo_variants?.thumbnail || null
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null)
  const [logoCropInfo, setLogoCropInfo] = useState(null)
  const [logoRemoved, setLogoRemoved] = useState(false)
  const [logoLoadFailed, setLogoLoadFailed] = useState(false)

  // When a file is pending show the local blob preview; otherwise reflect the server-saved logo (unless removed/broken)
  const savedLogoDisplayUrl = logoRemoved || logoLoadFailed ? null : savedLogoUrl
  const displayLogoUrl = logoFile ? logoPreviewUrl : savedLogoDisplayUrl

  const tabSaveDisabled = {
    [TABS.theme]: mode === prevModeRef.current,
    [TABS.customBranding]: !logoFile && !logoRemoved,
  }

  const tabItems = [
    { key: TABS.customBranding, label: t("branding.tabs.custom_branding") },
    { key: TABS.theme, label: t("branding.tabs.theme") },
  ]

  const onSelect = (theme) => {
    setMode(theme)
    switchTheme(theme)
  }

  const handleCancel = () => {
    if (activeTab === TABS.theme) {
      if (prevModeRef.current !== mode) {
        switchTheme(themes[prevModeRef.current])
        setMode(prevModeRef.current)
      }
    } else {
      setLogoFile(null)
      setLogoPreviewUrl(null)
      setLogoCropInfo(null)
      setLogoRemoved(false)
      setLogoLoadFailed(false)
    }
  }

  const handleLogoChange = (mediaList) => {
    const media = mediaList[0]
    if (!media) return

    const file = media.originalFile || media.file || media
    const previewUrl = media.file_preview || media.url || media.preview_url || null

    const img = new window.Image()
    img.onload = () => {
      if (img.naturalWidth < 400 || img.naturalHeight < 400) {
        toaster.error(t("branding.custom_branding.logo_too_small"))
        return
      }
      setLogoFile(file)
      setLogoPreviewUrl(previewUrl)
      setLogoCropInfo(media.cropInfo || null)
      toaster.success(t("branding.custom_branding.logo_uploaded"))
    }
    img.src = previewUrl
  }

  const editFileInputRef = useRef(null)
  const [editCropVisible, setEditCropVisible] = useState(false)
  const [editCropSrc, setEditCropSrc] = useState(null)
  const [editPendingFile, setEditPendingFile] = useState(null)

  const handleEditClick = () => {
    editFileInputRef.current?.click()
  }

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    if (file.size / 1024 / 1024 > 5) {
      toaster.error(t("media_upload.messages.image_size_limit_exceeded", { maxSize: 5 }))
      return
    }
    const objectUrl = URL.createObjectURL(file)
    const img = new window.Image()
    img.onload = () => {
      if (img.naturalWidth < 400 || img.naturalHeight < 400) {
        toaster.error(t("branding.custom_branding.logo_too_small"))
        URL.revokeObjectURL(objectUrl)
        return
      }
      setEditCropSrc(objectUrl)
      setEditPendingFile(file)
      setEditCropVisible(true)
    }
    img.onerror = () => URL.revokeObjectURL(objectUrl)
    img.src = objectUrl
  }

  const closeEditCropper = () => {
    if (editCropSrc) URL.revokeObjectURL(editCropSrc)
    setEditCropSrc(null)
    setEditPendingFile(null)
    setEditCropVisible(false)
  }

  const handleEditCropComplete = (croppedBlob, cropInfo) => {
    if (!croppedBlob || !editPendingFile) {
      closeEditCropper()
      return
    }
    const base = (editPendingFile.name || "image").replace(/\.[^/.]+$/, "")
    const croppedFile = new File([croppedBlob], `${base}-cropped.jpg`, { type: "image/jpeg" })
    const previewUrl = URL.createObjectURL(croppedBlob)
    const img = new window.Image()
    img.onload = () => {
      if (img.naturalWidth < 400 || img.naturalHeight < 400) {
        toaster.error(t("branding.custom_branding.logo_too_small"))
        URL.revokeObjectURL(previewUrl)
        closeEditCropper()
        return
      }
      setLogoFile(croppedFile)
      setLogoPreviewUrl(previewUrl)
      setLogoCropInfo(cropInfo || null)
      setLogoRemoved(false)
      closeEditCropper()
    }
    img.src = previewUrl
  }

  const submitTheme = () => {
    if (mode === prevModeRef.current) return
    dispatch(updateUser({ adventure_mode: mode })).then(() => {
      saveThemeToStorage(mode)
      prevModeRef.current = mode
      toaster.success(t("branding.save_success"))
    })
  }

  const submitCustomBranding = () => {
    if (!logoFile && !logoRemoved) return
    const formData = new FormData()
    if (logoFile) {
      formData.append("guide[logo]", logoFile)
      if (logoCropInfo) {
        formData.append("guide[logo_variant][x]", logoCropInfo.x)
        formData.append("guide[logo_variant][y]", logoCropInfo.y)
        formData.append("guide[logo_variant][width]", logoCropInfo.width)
        formData.append("guide[logo_variant][height]", logoCropInfo.height)
      }
    } else {
      formData.append("guide[remove_logo]", true)
    }
    dispatch(updateGuide(guide?.id, formData)).then(() => {
      setLogoFile(null)
      setLogoCropInfo(null)
      setLogoRemoved(false)
      dispatch(getUser())
      toaster.success(t("branding.save_success"))
    })
  }

  const tabSubmitHandlers = {
    [TABS.theme]: submitTheme,
    [TABS.customBranding]: submitCustomBranding,
  }

  const submitAdventureMode = () => {
    tabSubmitHandlers[activeTab]?.()
  }

  return (
    <div className="flex-column">
      <SettingsTitle subTitle={t("branding.subtitle")} />

      <DSTab items={tabItems} activeKey={activeTab} onChange={setActiveTab} className="mt-m" />

      <div className="flex-column mt-l">
        {activeTab === TABS.theme && (
          <div className="justify-center">
            <Row gutter={24} className="w-full">
              <Col span={12}>
                <Card
                  type={themes.sportsman}
                  bordered={false}
                  selected={mode === themes.sportsman}
                  onSelect={() => onSelect(themes.sportsman)}
                  isRadioButton
                />
              </Col>
              <Col span={12}>
                <Card
                  type={themes.sportswoman}
                  bordered={false}
                  selected={mode === themes.sportswoman}
                  onSelect={() => onSelect(themes.sportswoman)}
                  isRadioButton
                />
              </Col>
            </Row>
          </div>
        )}

        {activeTab === TABS.customBranding && (
          <div className="flex-column gap-s">
            <SettingsTitle subTitle={t("branding.custom_branding.subtitle")} />
            {displayLogoUrl ? (
              <div className={styles.logoPreviewWrapper}>
                <img
                  src={displayLogoUrl}
                  alt={guide?.company_name || t("branding.custom_branding.logo_preview_alt")}
                  className={styles.logoPreviewImage}
                  onError={() => setLogoLoadFailed(true)}
                />
                <button type="button" className={styles.logoEditOverlay} onClick={handleEditClick} aria-label={t("branding.custom_branding.edit_logo")}>
                  <Icon name="Edit2" width={20} height={20} color="white" />
                </button>
                <button
                  type="button"
                  className={styles.logoRemoveButton}
                  onClick={() => { setLogoRemoved(true); setLogoFile(null); setLogoPreviewUrl(null); setLogoCropInfo(null) }}
                  aria-label={t("branding.custom_branding.remove_logo")}
                >
                  <Icon name="X" width={12} height={12} color="white" />
                </button>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/png,image/jpg,image/jpeg"
                  style={{ display: "none" }}
                  onChange={handleEditFileChange}
                />
              </div>
            ) : (
              <MediaUpload
                acceptedTypes={["image/png", "image/jpg", "image/jpeg"]}
                imageInfo={t("branding.custom_branding.upload_hint")}
                onFileListChange={handleLogoChange}
                maxCount={1}
                maxImageSize={5}
                requireImageCrop
                cropAspect={null}
                lockCropSize={false}
                showUploadSuccessMessage={false}
                cropMinNaturalWidth={400}
                cropMinNaturalHeight={400}
                onDimensionError={() => toaster.error(t("branding.custom_branding.logo_too_small"))}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex gap-s mt-4xl">
        <ActionButtons onCancelClick={tabSaveDisabled[activeTab] ? undefined : handleCancel} onSaveClick={submitAdventureMode} loading={loading} disabled={tabSaveDisabled[activeTab]} />
      </div>

      {editCropVisible && (
        <ImageCropper
          visible={editCropVisible}
          onClose={closeEditCropper}
          imageSrc={editCropSrc || ""}
          onCropComplete={handleEditCropComplete}
          aspect={null}
          lockSize={false}
          minNaturalWidth={400}
          minNaturalHeight={400}
        />
      )}
    </div>
  )
}
